import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Spinner, Alert, Modal } from "react-bootstrap";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSync,
  FaUnlink,
} from "react-icons/fa";
import { lightspeedService } from "../../../services/lightspeedService";
import {
  LightspeedConnectionStatusPayload,
  StoreConnectionStatusProps,
} from "../../types/lightspeed.types";
import {
  handleLightspeedError,
  LightspeedError,
} from "../../utils/lightspeedErrorHandler";
import "./lightspeed-components.scss";

export const StoreConnectionStatus: React.FC<StoreConnectionStatusProps> = ({
  storeId,
  onDisconnect,
  onStatusChange,
  className = "",
}) => {
  const [status, setStatus] = useState<{
    loading: boolean;
    connected: boolean;
    data: LightspeedConnectionStatusPayload | null;
    error: LightspeedError | null;
  }>({
    loading: true,
    connected: false,
    data: null,
    error: null,
  });
  const [isRechecking, setIsRechecking] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!storeId) return;

    setIsRechecking(true);
    setStatus((prev) => ({ ...prev, error: null }));

    try {
      const result = await lightspeedService.checkConnectionStatus(storeId);
      const data = result?.data ?? null;

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch connection status");
      }

      const isConnected = !!(
        data &&
        (data.isConnected === true || data.status === "connected")
      );

      setStatus({
        loading: false,
        connected: isConnected,
        data,
        error: null,
      });
    } catch (err) {
      const lightspeedError = handleLightspeedError(err);
      setStatus((prev) => ({
        ...prev,
        loading: false,
        error: lightspeedError,
      }));
    } finally {
      setIsRechecking(false);
    }
  }, [storeId]);

  useEffect(() => {
    onStatusChange?.(status.connected);
  }, [status.connected, onStatusChange]);



  const confirmDisconnect = async () => {
    if (!storeId) {
      console.error("Cannot disconnect: storeId is required");
      return;
    }

    setIsDisconnecting(true);
    try {
      const result = await lightspeedService.disconnectStore(storeId);
      if (result.success) {
        setShowDisconnectModal(false);
        await fetchStatus();
        onDisconnect?.();
      } else {
        throw new Error(result.error ?? "Failed to disconnect");
      }
    } catch (err) {
      const lightspeedError = handleLightspeedError(err);
      setStatus((prev) => ({
        ...prev,
        error: lightspeedError,
      }));
    } finally {
      setIsDisconnecting(false);
    }
  };

  useEffect(() => {
    const initialFetch = async () => {
      setStatus((prev) => ({ ...prev, loading: true }));
      await fetchStatus();
      setStatus((prev) => ({ ...prev, loading: false }));
    };
    initialFetch();
  }, [storeId, fetchStatus]);

  useEffect(() => {
    if (!storeId) return;

    setStatus((prev) => ({ ...prev, loading: true }));
    fetchStatus();
  }, [storeId, fetchStatus]);


  const formatDate = (dateString?: string) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  if (status.loading) {
    return (
      <div className={`loading-state ${className}`}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Checking connection status...</p>
      </div>
    );
  }

  if (status.error) {
    return (
      <Alert
        variant="danger"
        className={`lightspeed-status-error ${className}`}
      >
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2">
            <FaExclamationTriangle />
            <span>
              Error: {status.error.userMessage || status.error.message}
            </span>
          </div>
          <div className="status-card__actions">
            <button
              className="button button--secondary button--small"
              onClick={fetchStatus}
              disabled={isRechecking}
            >
              {isRechecking ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : (
                "Recheck"
              )}
            </button>
          </div>
        </div>
      </Alert>
    );
  }

  return (
    <>
      <Card className={`lightspeed-status-card ${className}`}>
        <Card.Body>
          <div className="status-header">
            <h5>
              {status.connected ? (
                <>
                  <FaCheckCircle className="status-icon connected" />
                  Connected to Lightspeed
                </>
              ) : (
                <>
                  <FaExclamationTriangle className="status-icon disconnected" />
                  Not Connected to Lightspeed
                </>
              )}
            </h5>

            <div className="status-actions">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchStatus}
                disabled={isRechecking}
              >
                {isRechecking ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-1"
                  />
                ) : (
                  <FaSync className="me-1" />
                )}
                Recheck
              </Button>

              {status.connected && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setShowDisconnectModal(true)}
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-1"
                    />
                  ) : (
                    <FaUnlink className="me-1" />
                  )}
                  Disconnect
                </Button>
              )}
            </div>
          </div>

          {status.connected && status.data && (
            <div className="status-details">
              <div className="detail-row">
                <span className="detail-label">Store:</span>
                <span className="detail-value">
                  {status.data.storeName ||
                    status.data.lightspeedStoreId ||
                    "N/A"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Account ID:</span>
                <span className="detail-value">
                  {status.data.lightspeedAccountId ||
                    status.data.lightspeedRetailerId ||
                    "N/A"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Updated:</span>
                <span className="detail-value">
                  {formatDate(
                    status.data.lastUpdated || status.data.lastSyncAt
                  )}
                </span>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
      <Modal
        show={showDisconnectModal}
        onHide={() => setShowDisconnectModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Disconnect Lightspeed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to disconnect from Lightspeed? This will stop
            all syncing of products, inventory, and orders.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDisconnectModal(false)}
            disabled={isDisconnecting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDisconnect}
            disabled={isDisconnecting}
          >
            {isDisconnecting ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                className="me-1"
              />
            ) : null}
            Disconnect
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StoreConnectionStatus;
